import React from 'react';
import { useIsDisabledByPermission } from '../contexts/PermissionAccessContext';

export type ThreeStateValue = 'no' | 'neutral' | 'yes';
export type ThreeStateSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ThreeStateToggleProps {
  value?: ThreeStateValue;
  onChange?: (value: ThreeStateValue) => void;
  size?: ThreeStateSize;
  name?: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  labels?: {
    no?: string;
    neutral?: string;
    yes?: string;
  };
}

export default function ThreeStateToggle({
  value = 'neutral',
  onChange,
  size = 'md',
  name = 'three-state-toggle',
  ariaLabel = 'Three state toggle',
  disabled: disabledProp = false,
  className = '',
  labels = { no: 'No', neutral: 'No Pref', yes: 'Yes' }
}: ThreeStateToggleProps) {
  const disabledByPermission = useIsDisabledByPermission();
  const disabled = disabledProp || disabledByPermission;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || !onChange) return;

    // Get click position relative to the button
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;

    // Divide button into 3 equal sections
    const sectionWidth = width / 3;

    if (clickX < sectionWidth) {
      // Clicked left section
      onChange('no');
    } else if (clickX < sectionWidth * 2) {
      // Clicked middle section
      onChange('neutral');
    } else {
      // Clicked right section
      onChange('yes');
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'xs':
        return {
          container: 'w-12 h-4',
          knob: 'size-3',
          translateLeft: 'translate-x-0.5',
          translateCenter: 'translate-x-[17px]',
          translateRight: 'translate-x-[33px]'
        };
      case 'sm':
        return {
          container: 'w-16 h-6',
          knob: 'size-4',
          translateLeft: 'translate-x-1',
          translateCenter: 'translate-x-6',
          translateRight: 'translate-x-11'
        };
      case 'md':
        return {
          container: 'w-20 h-8',
          knob: 'size-6',
          translateLeft: 'translate-x-1',
          translateCenter: 'translate-x-7',
          translateRight: 'translate-x-13'
        };
      case 'lg':
        return {
          container: 'w-24 h-10',
          knob: 'size-8',
          translateLeft: 'translate-x-1',
          translateCenter: 'translate-x-8',
          translateRight: 'translate-x-15'
        };
      default:
        return {
          container: 'w-20 h-8',
          knob: 'size-6',
          translateLeft: 'translate-x-1',
          translateCenter: 'translate-x-7',
          translateRight: 'translate-x-13'
        };
    }
  };

  const sizeClasses = getSizeClasses();

  // Get position class based on value
  const getPositionClass = () => {
    if (value === 'no') return sizeClasses.translateLeft;
    if (value === 'yes') return sizeClasses.translateRight;
    return sizeClasses.translateCenter;
  };

  // Get background color based on value
  const getBackgroundColor = () => {
    if (value === 'no') return 'bg-red-200 dark:bg-red-900';
    if (value === 'yes') return 'bg-green-200 dark:bg-green-900';
    return 'bg-gray-200 dark:bg-gray-700';
  };

  // Get knob color based on value
  const getKnobColor = () => {
    if (value === 'no') return 'bg-red-500';
    if (value === 'yes') return 'bg-green-500';
    return 'bg-gray-400';
  };

  const getCurrentLabel = () => {
    if (value === 'no') return labels.no;
    if (value === 'yes') return labels.yes;
    return labels.neutral;
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-label={ariaLabel}
        className={`group relative inline-flex ${sizeClasses.container} shrink-0 rounded-full p-1 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out ${getBackgroundColor()} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
      >
        <span className={`${sizeClasses.knob} rounded-full ${getKnobColor()} shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out ${getPositionClass()}`}></span>
      </button>
      <span className="text-xs text-gray-600 dark:text-gray-400 min-w-[80px]">
        {getCurrentLabel()}
      </span>
    </div>
  );
}
